import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PromoCodeInput from '../components/PromoCodeInput';
import { useCartStore } from '../store/cartStore';
import { useShippingInfo } from '../hooks/useShippingInfo';
import { formatPrice } from '../lib/utils';
import { trackPageView, trackBeginCheckout } from '../lib/analytics';

export default function Cart() {
  const navigate = useNavigate();
  const items = useCartStore((s) => s.items);
  const updateQuantity = useCartStore((s) => s.updateQuantity);
  const removeItem = useCartStore((s) => s.removeItem);
  const subtotal = useCartStore((s) => s.subtotal());
  const discount = useCartStore((s) => s.discount());
  const finalTotal = useCartStore((s) => s.finalTotal());
  const { label: shippingLabel } = useShippingInfo(finalTotal);

  useEffect(() => {
    trackPageView('/cart');
  }, []);

  const handleCheckout = () => {
    trackBeginCheckout(finalTotal, items.reduce((sum, i) => sum + i.quantity, 0));
    navigate('/checkout');
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="bg-x1493-dark text-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold">購物車</h1>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          {items.length === 0 ? (
            <div className="text-center py-16 bg-stone-50 rounded-2xl">
              <ShoppingBag size={64} className="mx-auto text-stone-300 mb-4" />
              <p className="text-lg text-stone-500 mb-6">購物車是空的</p>
              <Link to="/store" className="btn-primary">
                去商店逛逛
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {items.map((item) => (
                <div key={item.productId} className="flex gap-4 bg-stone-50 p-4 rounded-2xl">
                  <div className="w-24 h-24 rounded-xl bg-white overflow-hidden flex-shrink-0">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-3xl">🌶️</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg">{item.name}</h3>
                    <p className="text-x1493-primary font-semibold">{formatPrice(item.unitPrice)}</p>
                    <div className="flex items-center gap-3 mt-3">
                      <button
                        onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                        className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center hover:bg-stone-100"
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-6 text-center font-bold">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                        className="w-8 h-8 rounded-lg bg-white border flex items-center justify-center hover:bg-stone-100"
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col justify-between items-end">
                    <button
                      onClick={() => removeItem(item.productId)}
                      className="text-stone-400 hover:text-red-500 p-1"
                    >
                      <Trash2 size={20} />
                    </button>
                    <span className="font-bold text-lg">{formatPrice(item.subtotal)}</span>
                  </div>
                </div>
              ))}

              <div className="bg-stone-50 rounded-2xl p-6 space-y-4">
                <PromoCodeInput orderAmount={subtotal} />

                <div className="border-t border-stone-200 pt-4 space-y-2">
                  <div className="flex justify-between text-stone-600">
                    <span>小計</span>
                    <span>{formatPrice(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>優惠</span>
                      <span>-{formatPrice(discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-stone-600">
                    <span>運費</span>
                    <span>{shippingLabel}</span>
                  </div>
                  <div className="flex justify-between text-2xl font-bold">
                    <span>合計</span>
                    <span className="text-x1493-primary">{formatPrice(finalTotal)}</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Link
                    to="/store"
                    className="flex-1 text-center py-3 rounded-xl font-bold border-2 border-stone-200 text-stone-700 hover:bg-stone-100 transition"
                  >
                    繼續購物
                  </Link>
                  <button
                    onClick={handleCheckout}
                    className="flex-[2] bg-x1493-primary text-white py-3 rounded-xl font-bold hover:bg-red-700 transition flex items-center justify-center gap-2"
                  >
                    前往結帳
                    <ArrowRight size={20} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
